import React, { useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/icons-material/Menu';
import Home from '@mui/icons-material/Home';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Settings from '@mui/icons-material/Settings';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Assessment from '@mui/icons-material/Assessment';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService, AuthUser } from '../../services/authService';
import { demoAuthService } from '../../services/demoAuthService';
import { isDemoMode } from '../../config';

interface NavigationPaneProps {
  isExpanded: boolean;
  onToggle: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const menuItems: MenuItem[] = [
  { path: '/', label: 'Home', icon: <Home /> },
  { path: '/upload', label: 'Upload', icon: <CloudUpload /> },
];

const adminMenuItems: MenuItem[] = [{ path: '/settings', label: 'Settings', icon: <Settings /> }];

export const NavigationPane: React.FC<NavigationPaneProps> = ({ isExpanded, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    // Fetch DB-sourced role so navigation reflects actual permissions
    authService.getUserAsync().then(u => setUser(u));
  }, []);

  const handleNavigation = (path: string) => {
    // Handle demo user restrictions
    if (isDemoMode) {
      if (path === '/upload' && !demoAuthService.canUploadFiles()) {
        window.open('/demo/upload', '_blank');
        return;
      }
      if (path === '/settings' && !demoAuthService.canAccessSettings()) {
        window.open('/demo/settings', '_blank');
        return;
      }
    }
    navigate(path);
  };

  // Filter menu items based on user role and demo permissions
  const getVisibleMenuItems = () => {
    let items = menuItems;

    // PlatformAdmin sees only the admin landing page — no tenant call pages
    if (user?.role === 'PlatformAdmin') return [
      { path: '/admin', label: 'Tenants', icon: <AdminPanelSettings /> },
    ];

    // Agents can view calls but not upload
    if (user?.role === 'Agent') {
      items = items.filter((item) => item.path !== '/upload');
    }

    // Supervisors and TenantAdmins get Reports access
    if (user?.role === 'Supervisor' || user?.role === 'TenantAdmin') {
      items = [...items, { path: '/reports', label: 'Reports', icon: <Assessment /> }];
    }

    if (!isDemoMode) return items;

    return items.filter((item) => {
      if (item.path === '/upload') return demoAuthService.canUploadFiles();
      return true;
    });
  };

  const getVisibleAdminItems = () => {
    // PlatformAdmin accesses settings via tenant cards on /admin — no separate Settings link
    if (user?.role === 'PlatformAdmin') return [];

    // Only show Settings for non-PlatformAdmin roles that need it (none currently)
    if (!isDemoMode) return [];

    return adminMenuItems.filter((item) => {
      if (item.path === '/settings') return demoAuthService.canAccessSettings();
      return true;
    });
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        position: 'fixed',
        zIndex: 1200,
        '& .MuiDrawer-paper': {
          width: isExpanded ? 240 : 64,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          borderRight: 1,
          borderColor: 'divider',
          position: 'fixed',
          height: '100vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          minHeight: 64,
        }}
      >
        <IconButton
          onClick={onToggle}
          aria-label={isExpanded ? 'Collapse navigation' : 'Expand navigation'}
        >
          {isExpanded ? <ChevronLeft /> : <Menu />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, pt: 1 }}>
        {getVisibleMenuItems().map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                minHeight: 48,
                justifyContent: isExpanded ? 'initial' : 'center',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isExpanded ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {isExpanded && <ListItemText primary={item.label} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Admin Settings Section */}
      {getVisibleAdminItems().length > 0 && (
        <List sx={{ pt: 1 }}>
          {getVisibleAdminItems().map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  minHeight: 48,
                  justifyContent: isExpanded ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isExpanded ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isExpanded && <ListItemText primary={item.label} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      <Divider />

      {/* User Profile Section */}
      <Box sx={{ p: 1 }}>
        <Box
          onClick={() => handleNavigation('/profile')}
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            borderRadius: 1,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
            }}
          >
            {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          {isExpanded && (
            <Box sx={{ ml: 2, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {user?.displayName || 'Loading...'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.role || 'User'} • {user?.email || 'Loading...'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Logout Button for Demo Mode */}
        {isDemoMode && isExpanded && (
          <Box sx={{ mt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={async () => {
                await authService.logout();
                navigate('/login');
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              Sign Out
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
